/**
 * Phase 3F: CPT Sequencing Engine
 * 
 * Rule-based code-string builder for final export CPT lines.
 * - Ranks CPTs by reimbursement priority
 * - Primary highest value at 100%
 * - Ancillaries sequenced for correct multiple procedure reductions
 * - Applies modifiers with evidence linking
 */

import { BWC_2026_SAMPLE_DATA, FeeScheduleRow } from '@/lib/bwc/feeScheduleData';
import { 
  applyModifiers, 
  buildCodeString,
  type ModifierSequencingInput,
  type ModifierApplication,
} from '@/lib/modifiers/modifierSequencing';
import { getCptTag } from '@/lib/modifiers/cptTagMap';
import type { SiteOfService, Laterality } from '@/lib/modifiers/types';
import type { EvidenceSnippet } from './types';

// ============================================================================
// TYPES
// ============================================================================

export interface BillableReconciliationItem {
  id: string;
  cptCode: string;
  cptDescription?: string;
  evidenceSnippets: EvidenceSnippet[];
  documentationText?: string;
  laterality: Laterality | null;
  siteOfService: SiteOfService | null;
  existingModifiers?: string[];
  sequence?: number;
}

export interface SequencedCptLine {
  cptCode: string;
  cptDescription?: string;
  sequence: number;
  isPrimary: boolean;
  baseReimbursement: number;
  effectiveReimbursement: number;
  reductionPercent: number;
  modifiers: string[];
  modifierApplications: ModifierApplicationWithEvidence[];
  finalCodeString: string;
  units: number;
  warnings: string[];
  reviewRequired: boolean;
  reviewReasons: string[];
}

export interface ModifierApplicationWithEvidence extends ModifierApplication {
  evidenceReference?: string;
  evidenceSnippetId?: string;
}

export interface SequencingResult {
  lines: SequencedCptLine[];
  totalEstimatedReimbursement: number;
  hasBlockingIssues: boolean;
  blockingReasons: string[];
  warnings: string[];
  auditTrail: SequencingAuditEntry[];
}

export interface SequencingAuditEntry {
  action: string;
  cptCode: string;
  details: string;
  timestamp: string;
}

// ============================================================================
// MULTIPLE PROCEDURE REDUCTION RULES
// ============================================================================

/**
 * Standard CMS multiple procedure reduction percentages
 * Primary procedure: 100%
 * Second procedure: 50%
 * Third+: 50% each
 */
const MULTIPLE_PROCEDURE_REDUCTIONS: Record<number, number> = {
  1: 100,   // Primary: full reimbursement
  2: 50,    // Second: 50%
  3: 50,    // Third: 50%
  4: 50,    // Fourth: 50%
  5: 50,    // Fifth+: 50%
};

function getReductionPercent(sequence: number): number {
  return MULTIPLE_PROCEDURE_REDUCTIONS[Math.min(sequence, 5)] || 50;
}

// ============================================================================
// FEE SCHEDULE LOOKUP
// ============================================================================

// Build lookup map from sample data
const feeScheduleMap = new Map<string, FeeScheduleRow>();
BWC_2026_SAMPLE_DATA.forEach(row => {
  feeScheduleMap.set(row.cpt_code, row);
});

/**
 * Get reimbursement amount for a CPT code
 * Uses facility fee as default for hospital/ASC setting
 */
export function getCptReimbursement(
  cptCode: string, 
  siteOfService: SiteOfService | null
): number {
  const entry = feeScheduleMap.get(cptCode);
  if (!entry) return 0;
  
  if (siteOfService === 'office') {
    return entry.non_facility_fee || 0;
  }
  // Hospital or ASC - use facility fee
  return entry.facility_fee || 0;
}

/**
 * Fallback priority weights for CPTs not in fee schedule
 * Higher weight = higher priority
 */
const CPT_PRIORITY_WEIGHTS: Record<string, number> = {
  // Major joint replacements
  '27447': 10000, // TKA
  '27130': 9500,  // THA
  '23472': 9000,  // TSA
  
  // Spine fusions
  '22612': 9000,  // Lumbar fusion
  '22551': 8500,  // ACDF
  
  // Reconstructive
  '29888': 7000,  // ACL
  '29889': 7000,  // PCL
  
  // Arthroscopy with repair
  '29882': 5000,  // Meniscus repair
  '29827': 5000,  // RTC repair
  
  // Arthroscopy diagnostic/debridement
  '29881': 3000,  // Meniscectomy
  '29877': 2500,  // Chondroplasty
  '29826': 2500,  // SAD
  
  // Minor procedures
  '64721': 2000,  // CTR
  '26055': 1500,  // Trigger finger
};

function getPriorityWeight(cptCode: string, reimbursement: number): number {
  if (reimbursement > 0) return reimbursement;
  return CPT_PRIORITY_WEIGHTS[cptCode] || 100;
}

// ============================================================================
// MODIFIER MUTUAL EXCLUSION RULES
// ============================================================================

interface ModifierConflict {
  modifiers: string[];
  reason: string;
}

const MODIFIER_CONFLICTS: ModifierConflict[] = [
  { modifiers: ['RT', 'LT'], reason: 'Cannot use both RT and LT on same procedure' },
  { modifiers: ['RT', '50'], reason: 'Cannot use RT with bilateral modifier 50' },
  { modifiers: ['LT', '50'], reason: 'Cannot use LT with bilateral modifier 50' },
  { modifiers: ['26', 'TC'], reason: 'Cannot use both professional (26) and technical (TC) components' },
  { modifiers: ['59', 'XE'], reason: 'XE is more specific - use instead of 59' },
  { modifiers: ['59', 'XS'], reason: 'XS is more specific - use instead of 59' },
  { modifiers: ['59', 'XP'], reason: 'XP is more specific - use instead of 59' },
  { modifiers: ['59', 'XU'], reason: 'XU is more specific - use instead of 59' },
];

/**
 * Check for modifier conflicts
 */
function checkModifierConflicts(modifiers: string[]): string[] {
  const errors: string[] = [];
  const modSet = new Set(modifiers);
  
  for (const conflict of MODIFIER_CONFLICTS) {
    const matchCount = conflict.modifiers.filter(m => modSet.has(m)).length;
    if (matchCount > 1) {
      errors.push(conflict.reason);
    }
  }
  
  return errors;
}

// ============================================================================
// DISTINCT PROCEDURAL SERVICE DETECTION
// ============================================================================

/**
 * Check if documentation supports 59/XS modifier for distinct service
 */
function checkDistinctServiceEvidence(
  documentationText: string,
  evidenceSnippets: EvidenceSnippet[]
): { supported: boolean; evidenceReference?: string } {
  const distinctKeywords = [
    'separate anatomic site',
    'separate incision',
    'separate compartment',
    'different anatomic structure',
    'distinct procedure',
    'separate structure',
    'additional compartment',
    'different compartment',
    'separate organ',
  ];
  
  const docLower = documentationText.toLowerCase();
  
  for (const keyword of distinctKeywords) {
    if (docLower.includes(keyword)) {
      return {
        supported: true,
        evidenceReference: `"${keyword}" found in documentation`,
      };
    }
  }
  
  // Also check evidence snippets
  for (const snippet of evidenceSnippets) {
    for (const keyword of distinctKeywords) {
      if (snippet.text.toLowerCase().includes(keyword)) {
        return {
          supported: true,
          evidenceReference: `Evidence snippet: "${snippet.text.substring(0, 50)}..."`,
        };
      }
    }
  }
  
  return { supported: false };
}

// ============================================================================
// ASSISTANT SURGEON DETECTION
// ============================================================================

const ASSISTANT_SURGEON_KEYWORDS = [
  'assistant surgeon',
  'surgical assistant',
  'complexity requiring assistance',
  'two-surgeon approach',
  'prolonged duration',
  'extensive procedure',
  'required assistant',
];

/**
 * Check if documentation supports assistant surgeon modifier (80/AS)
 */
function checkAssistantSurgeonEvidence(
  documentationText: string
): { supported: boolean; evidenceReference?: string } {
  const docLower = documentationText.toLowerCase();
  
  for (const keyword of ASSISTANT_SURGEON_KEYWORDS) {
    if (docLower.includes(keyword)) {
      return {
        supported: true,
        evidenceReference: `"${keyword}" indicates assistant surgeon requirement`,
      };
    }
  }
  
  return { supported: false };
}

// ============================================================================
// MAIN SEQUENCING ENGINE
// ============================================================================

export interface SequencingOptions {
  siteOfService: SiteOfService;
  payerId?: string;
  planId?: string;
  isDemoMode?: boolean;
  applyMultipleProcedureReduction?: boolean;
}

/**
 * Build sequenced CPT lines from billable reconciliation items
 * 
 * @param items - Billable reconciliation items (surgeon confirmed)
 * @param options - Sequencing options
 * @returns Sequenced result with lines, totals, and audit trail
 */
export function buildSequencedCptLines(
  items: BillableReconciliationItem[],
  options: SequencingOptions
): SequencingResult {
  const auditTrail: SequencingAuditEntry[] = [];
  const warnings: string[] = [];
  const blockingReasons: string[] = [];
  
  const addAudit = (action: string, cptCode: string, details: string) => {
    auditTrail.push({
      action,
      cptCode,
      details,
      timestamp: new Date().toISOString(),
    });
  };

  // 1. Calculate reimbursement and priority for each item
  const itemsWithPriority = items.map(item => {
    const reimbursement = getCptReimbursement(item.cptCode, options.siteOfService);
    const priority = getPriorityWeight(item.cptCode, reimbursement);
    addAudit('PRIORITY_CALCULATED', item.cptCode, 
      `Reimbursement: $${reimbursement.toFixed(2)}, Priority: ${priority}`);
    return { item, reimbursement, priority };
  });

  // 2. Sort by priority (highest first) for sequencing
  itemsWithPriority.sort((a, b) => b.priority - a.priority);
  addAudit('SEQUENCE_ORDERED', 'ALL', 
    `Ordered ${itemsWithPriority.length} CPTs by priority`);

  // 3. Build sequenced lines with modifiers
  const lines: SequencedCptLine[] = itemsWithPriority.map((entry, index) => {
    const sequence = index + 1;
    const isPrimary = sequence === 1;
    const reductionPercent = options.applyMultipleProcedureReduction !== false 
      ? getReductionPercent(sequence) 
      : 100;
    const effectiveReimbursement = (entry.reimbursement * reductionPercent) / 100;
    
    addAudit('SEQUENCE_ASSIGNED', entry.item.cptCode, 
      `Sequence ${sequence}, ${isPrimary ? 'PRIMARY' : 'ANCILLARY'}, ${reductionPercent}% of base`);

    // Apply modifiers using sequencing engine
    const modifierInput: ModifierSequencingInput = {
      cptCode: entry.item.cptCode,
      siteOfService: options.siteOfService,
      laterality: entry.item.laterality,
      payerId: options.payerId,
      planId: options.planId,
      documentationText: entry.item.documentationText,
      existingModifiers: entry.item.existingModifiers,
    };
    
    const modifierResult = applyModifiers(modifierInput);
    
    // Enhance with evidence references
    const modifierApplications: ModifierApplicationWithEvidence[] = 
      modifierResult.appliedModifiers.map(app => {
        const enhanced: ModifierApplicationWithEvidence = { ...app };
        
        // Link evidence for documentation-based modifiers
        if (app.ruleSource === 'documentation' && entry.item.evidenceSnippets.length > 0) {
          enhanced.evidenceReference = `Based on documentation evidence`;
          enhanced.evidenceSnippetId = entry.item.evidenceSnippets[0]?.text.substring(0, 30);
        }
        
        return enhanced;
      });

    // Log modifier applications
    modifierApplications.forEach(app => {
      addAudit('MODIFIER_APPLIED', entry.item.cptCode, 
        `${app.modifier}: ${app.reason} (${app.ruleSource}, ${app.confidence})`);
    });

    // Check for 59/XS modifier need based on evidence
    if (!isPrimary && entry.item.documentationText) {
      const distinctCheck = checkDistinctServiceEvidence(
        entry.item.documentationText,
        entry.item.evidenceSnippets
      );
      
      if (distinctCheck.supported) {
        const has59orXS = modifierApplications.some(
          m => m.modifier === '59' || m.modifier === 'XS'
        );
        
        if (!has59orXS) {
          // Check if 59 is allowed
          const cptTag = getCptTag(entry.item.cptCode, options.siteOfService);
          if (cptTag.category === 'SURGICAL') {
            modifierApplications.push({
              modifier: '59',
              reason: `Distinct procedural service: ${distinctCheck.evidenceReference}`,
              ruleSource: 'documentation',
              confidence: 'medium',
              evidenceReference: distinctCheck.evidenceReference,
            });
            addAudit('MODIFIER_APPLIED', entry.item.cptCode, 
              `59: Distinct service evidence found`);
          }
        }
      }
    }

    // Check for assistant surgeon
    if (entry.item.documentationText) {
      const assistCheck = checkAssistantSurgeonEvidence(entry.item.documentationText);
      const cptTag = getCptTag(entry.item.cptCode, options.siteOfService);
      
      if (assistCheck.supported && cptTag.allows_assistant) {
        const has80 = modifierApplications.some(m => m.modifier === '80');
        if (!has80) {
          // Add as warning - requires confirmation checklist
          warnings.push(
            `CPT ${entry.item.cptCode}: Assistant surgeon documentation found. ` +
            `Add modifier 80 if confirmed with checklist.`
          );
          addAudit('ASSISTANT_SURGEON_DETECTED', entry.item.cptCode, 
            assistCheck.evidenceReference || 'Detected in documentation');
        }
      }
    }

    // Validate modifier combinations
    const modifierCodes = modifierApplications.map(m => m.modifier);
    const conflicts = checkModifierConflicts(modifierCodes);
    const lineWarnings = [...modifierResult.warnings, ...conflicts];
    
    // Determine review status
    const reviewReasons: string[] = [];
    let reviewRequired = false;
    
    if (conflicts.length > 0) {
      reviewRequired = true;
      reviewReasons.push(...conflicts);
      addAudit('REVIEW_REQUIRED', entry.item.cptCode, `Modifier conflicts: ${conflicts.join('; ')}`);
    }
    
    if (modifierResult.warnings.length > 0) {
      reviewReasons.push(...modifierResult.warnings);
    }

    // Build final code string
    const finalCodeString = buildCodeString(entry.item.cptCode, modifierCodes);
    addAudit('CODE_STRING_BUILT', entry.item.cptCode, finalCodeString);

    return {
      cptCode: entry.item.cptCode,
      cptDescription: entry.item.cptDescription,
      sequence,
      isPrimary,
      baseReimbursement: entry.reimbursement,
      effectiveReimbursement,
      reductionPercent,
      modifiers: modifierCodes,
      modifierApplications,
      finalCodeString,
      units: 1,
      warnings: lineWarnings,
      reviewRequired,
      reviewReasons,
    };
  });

  // 4. Check for blocking issues
  const linesWithReview = lines.filter(l => l.reviewRequired);
  const hasBlockingIssues = linesWithReview.length > 0 && !options.isDemoMode;
  
  if (hasBlockingIssues) {
    linesWithReview.forEach(line => {
      blockingReasons.push(
        `CPT ${line.cptCode} requires review: ${line.reviewReasons.join('; ')}`
      );
    });
    addAudit('EXPORT_BLOCKED', 'ALL', 
      `${linesWithReview.length} CPT(s) require review before export`);
  }

  // 5. Calculate totals
  const totalEstimatedReimbursement = lines.reduce(
    (sum, line) => sum + line.effectiveReimbursement, 
    0
  );
  addAudit('TOTAL_CALCULATED', 'ALL', 
    `Total estimated reimbursement: $${totalEstimatedReimbursement.toFixed(2)}`);

  return {
    lines,
    totalEstimatedReimbursement,
    hasBlockingIssues,
    blockingReasons,
    warnings,
    auditTrail,
  };
}

/**
 * Generate modifier explanation for UI tooltip
 */
export function getModifierExplanation(application: ModifierApplicationWithEvidence): string {
  let explanation = application.reason;
  
  if (application.evidenceReference) {
    explanation += ` (${application.evidenceReference})`;
  }
  
  return explanation;
}

/**
 * Format final CPT lines for export
 */
export function formatForExport(lines: SequencedCptLine[]): Array<{
  sequence: number;
  cpt: string;
  modifiers: string[];
  units: number;
  codeString: string;
}> {
  return lines.map(line => ({
    sequence: line.sequence,
    cpt: line.cptCode,
    modifiers: line.modifiers,
    units: line.units,
    codeString: line.finalCodeString,
  }));
}
